import type { ReactNode } from 'react';
import { Alert } from '@insurensa/ui';
import { useT } from '@insurensa/i18n';
import { ApiError } from '../lib/api.js';

/**
 * Console-specific pieces. Everything structural — cards, page titles, badges,
 * tables — comes from `@insurensa/ui`.
 */

/**
 * Renders an API failure. Validation errors arrive as a field list, so they are
 * shown itemised rather than as one opaque string — the legacy console
 * redirected with `?msg=Not Updated` and lost the detail entirely.
 */
export function ErrorAlert({ error }: { error: unknown }): JSX.Element | null {
  const t = useT();
  if (!error) return null;

  if (error instanceof ApiError) {
    return (
      <Alert tone="danger">
        <div>{error.message}</div>
        {error.fields.length > 0 && (
          <ul className="mb-0 mt-1 pl-3">
            {error.fields.map((field) => (
              <li key={`${field.path}:${field.message}`}>
                {field.path ? `${field.path}: ` : ''}
                {field.message}
              </li>
            ))}
          </ul>
        )}
        {error.requestId && (
          <div className="small text-muted mt-1">
            {t('common.reference')}: {error.requestId}
          </div>
        )}
      </Alert>
    );
  }

  return (
    <Alert tone="danger">
      {error instanceof Error ? error.message : t('common.unknownError')}
    </Alert>
  );
}

export function SuccessAlert({ children }: { children: ReactNode }): JSX.Element {
  return <Alert tone="success">{children}</Alert>;
}

/** Full-card loading placeholder. */
export function BlockSkeleton({ height = 160 }: { height?: number }): JSX.Element {
  return <span className="block-skeleton" style={{ height }} />;
}

/** Labelled form field with inline validation, on Bootstrap 4 markup. */
export function Field({
  id,
  label,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="position-relative form-group">
      <label htmlFor={id}>{label}</label>
      {children}
      {error && <div className="field-error">{error}</div>}
      {hint && !error && <small className="form-text text-muted">{hint}</small>}
    </div>
  );
}
